import Planogram, { indexToSlotLabel } from '@micromart/utils/lib/planogram'
import type { ToolCallLike } from '@/lib/assistant-tool-metadata'

export interface CabinetHeatmapCell {
	cellIndex: number
	row: number
	column: number
	slotLabel: string | null
	productName: string | null
	productId: number | null
	units: number
	revenueCents: number
	slotWidth: number
	pairIndices: number[]
	stockStatus?: 'out' | 'low' | 'ok' | 'empty_slot' | null
	quantity?: number | null
	visionRisk?: boolean
	visionRiskReason?: string | null
	swapPreview?: {
		fromProductName: string | null
		toProductName: string | null
	} | null
}

export interface CabinetVisionWarning {
	slotA: string
	slotB: string
	productAName: string
	productBName: string
	reason: string
	severity: 'high' | 'medium'
}

export interface CabinetSwapPreview {
	cellIndex: number
	slotLabel: string | null
	fromProductName: string | null
	toProductName: string | null
	rationale?: string | null
	currentRevenueCents?: number | null
	suggestedRevenueCents?: number | null
}

export interface CabinetAffinityEndpoint {
	cellIndex: number | null
	row: number | null
	column: number | null
	slotLabel: string | null
	productId: number
	productName: string
}

export type CabinetPairPlacement = 'adjacent' | 'same_row' | 'far' | 'missing'

export interface CabinetAffinityPair {
	index: number
	color: string
	productA: CabinetAffinityEndpoint
	productB: CabinetAffinityEndpoint
	lift: number
	cooccurOrders: number
	placement: CabinetPairPlacement
	suggestMove: string | null
}

export interface CabinetMapData {
	siteName?: string
	dateFrom?: string
	dateTo?: string
	currency?: string
	rows: CabinetHeatmapCell[][]
	maxRevenueCents: number
	maxUnits: number
	pairs: CabinetAffinityPair[]
	visionWarnings: CabinetVisionWarning[]
	swaps: CabinetSwapPreview[]
	stockoutCount: number
	lowStockCount: number
}

export const CABINET_PAIR_COLORS = [
	'#e91e63',
	'#9c27b0',
	'#3f51b5',
	'#00897b',
	'#ef6c00',
	'#6d4c41',
	'#546e7a',
] as const

interface LayoutCellJson {
	cell_index?: number
	row?: number | null
	column?: number | null
	slot_label?: string | null
	product_name?: string | null
	product_id?: number | null
	recipe_id?: number | null
	slot_width?: number
}

interface PerformanceCellJson {
	cell_index?: number
	row?: number | null
	column?: number | null
	units?: number
	revenue_cents?: number
	product_name?: string | null
	recipe_name?: string | null
	product_id?: number | null
	recipe_id?: number | null
}

interface AffinitySlotJson {
	slot_label?: string
	row?: number
	column?: number
}

interface AffinityPlacementJson {
	product_id?: number
	product_name?: string
	recipe_id?: number
	recipe_name?: string
	slots?: AffinitySlotJson[]
}

interface AffinityResultJson {
	product_ids?: number[]
	product_names?: string[]
	lift?: number
	cooccur_orders?: number
	placements?: AffinityPlacementJson[]
	placement_summary?: {
		both_in_layout?: boolean
		adjacent?: boolean
		same_row?: boolean
		shelves_apart?: number | null
	}
	suggest_move?: string | null
	placements_by_cabinet?: Array<{
		site_id?: string
		site_name?: string
		placements?: AffinityPlacementJson[]
		placement_summary?: AffinityResultJson['placement_summary']
		suggest_move?: string | null
	}>
}

function parseToolJson(result: string | undefined): Record<string, unknown> | null {
	if (!result) return null
	try {
		const parsed = JSON.parse(result)
		return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
	} catch {
		return null
	}
}

function productIdFromCell(cell: { product_id?: number | null; recipe_id?: number | null }): number | null {
	const id = cell.product_id ?? cell.recipe_id
	return id != null && Number.isFinite(Number(id)) ? Number(id) : null
}

function performanceCellsFromResult(parsed: Record<string, unknown>): PerformanceCellJson[] | null {
	if (Array.isArray(parsed.sites)) return null
	const cells = parsed.cells
	if (!Array.isArray(cells)) return null
	return cells as PerformanceCellJson[]
}

function layoutCellsFromResult(parsed: Record<string, unknown>): {
	cells: LayoutCellJson[]
	layout: string | null
	siteName?: string
	siteId?: string
} | null {
	const cells = parsed.current_cells
	if (!Array.isArray(cells) || cells.length === 0) return null
	return {
		cells: cells as LayoutCellJson[],
		layout: typeof parsed.layout === 'string' ? parsed.layout : null,
		siteName: typeof parsed.site_name === 'string' ? parsed.site_name : undefined,
		siteId: typeof parsed.site_id === 'string' ? parsed.site_id : undefined,
	}
}

function perfByCellIndex(cells: PerformanceCellJson[]): Map<number, PerformanceCellJson> {
	const map = new Map<number, PerformanceCellJson>()
	for (const cell of cells) {
		if (cell.cell_index == null) continue
		map.set(Number(cell.cell_index), cell)
	}
	return map
}

function slotLabelForCell(
	row: number | null | undefined,
	column: number | null | undefined,
	fallback: string | null | undefined,
): string | null {
	if (fallback) return fallback
	if (row != null && column != null) return indexToSlotLabel(row, column)
	return null
}

function getCellSlotWidth(layoutString: string | null, cellIndex: number): number {
	if (!layoutString) return 1
	try {
		const layout = Planogram.getLayout(layoutString)
		const pos = Planogram.calculateCellPosition(layout, cellIndex)
		if (!pos) return 1
		const row = layout[pos.row]
		if (!row || pos.column >= row.length) return 1
		return Number(row[pos.column]) || 1
	} catch {
		return 1
	}
}

function buildRowsFromLayoutCells(
	layoutCells: LayoutCellJson[],
	layoutString: string | null,
	perfMap: Map<number, PerformanceCellJson>,
): CabinetHeatmapCell[][] {
	const merged = layoutCells
		.filter((c) => c.cell_index != null && c.row != null && c.column != null)
		.map((layoutCell) => {
			const cellIndex = Number(layoutCell.cell_index)
			const perf = perfMap.get(cellIndex)
			const productName = layoutCell.product_name
				?? perf?.product_name
				?? perf?.recipe_name
				?? null
			const productId = productIdFromCell(layoutCell) ?? productIdFromCell(perf ?? {})
			return {
				cellIndex,
				row: Number(layoutCell.row),
				column: Number(layoutCell.column),
				slotLabel: slotLabelForCell(layoutCell.row, layoutCell.column, layoutCell.slot_label),
				productName,
				productId,
				units: Number(perf?.units ?? 0),
				revenueCents: Number(perf?.revenue_cents ?? 0),
				slotWidth: Number(layoutCell.slot_width ?? getCellSlotWidth(layoutString, cellIndex)),
				pairIndices: [],
				stockStatus: null,
				quantity: null,
				visionRisk: false,
				visionRiskReason: null,
				swapPreview: null,
			}
		})

	return groupCellsIntoRows(merged)
}

function buildRowsFromPerformanceOnly(perfCells: PerformanceCellJson[]): CabinetHeatmapCell[][] {
	const merged = perfCells
		.filter((c) => c.cell_index != null && c.row != null && c.column != null)
		.map((perf) => ({
			cellIndex: Number(perf.cell_index),
			row: Number(perf.row),
			column: Number(perf.column),
			slotLabel: slotLabelForCell(perf.row, perf.column, null),
			productName: perf.product_name ?? perf.recipe_name ?? null,
			productId: productIdFromCell(perf),
			units: Number(perf.units ?? 0),
			revenueCents: Number(perf.revenue_cents ?? 0),
			slotWidth: 1,
			pairIndices: [],
			stockStatus: null,
			quantity: null,
			visionRisk: false,
			visionRiskReason: null,
			swapPreview: null,
		}))

	return groupCellsIntoRows(merged)
}

function groupCellsIntoRows(cells: CabinetHeatmapCell[]): CabinetHeatmapCell[][] {
	const byRow = new Map<number, CabinetHeatmapCell[]>()
	for (const cell of cells) {
		if (!byRow.has(cell.row)) byRow.set(cell.row, [])
		byRow.get(cell.row)!.push(cell)
	}
	return [...byRow.entries()]
		.sort(([a], [b]) => a - b)
		.map(([, rowCells]) => rowCells.sort((a, b) => a.column - b.column))
}

function finalizeMap(
	rows: CabinetHeatmapCell[][],
	meta: {
		siteName?: string
		dateFrom?: string
		dateTo?: string
		currency?: string
	},
	pairs: CabinetAffinityPair[],
	visionWarnings: CabinetVisionWarning[],
	swaps: CabinetSwapPreview[],
): CabinetMapData | null {
	if (!rows.length) return null
	let maxRevenueCents = 0
	let maxUnits = 0
	let stockoutCount = 0
	let lowStockCount = 0
	for (const row of rows) {
		for (const cell of row) {
			maxRevenueCents = Math.max(maxRevenueCents, cell.revenueCents)
			maxUnits = Math.max(maxUnits, cell.units)
			if (cell.stockStatus === 'out') stockoutCount += 1
			if (cell.stockStatus === 'low') lowStockCount += 1
		}
	}
	return {
		...meta,
		rows,
		maxRevenueCents,
		maxUnits,
		pairs,
		visionWarnings,
		swaps,
		stockoutCount,
		lowStockCount,
	}
}

function resolvePlacement(summary: AffinityResultJson['placement_summary']): CabinetPairPlacement {
	if (!summary?.both_in_layout) return 'missing'
	if (summary.adjacent) return 'adjacent'
	if (summary.same_row) return 'same_row'
	return 'far'
}

function cellIndexFromRowColumn(layoutString: string | null, row: number, column: number): number | null {
	if (!layoutString) return null
	try {
		const layout = Planogram.getLayout(layoutString)
		const total = Planogram.countCells(layout, false)
		for (let index = 0; index < total; index += 1) {
			const pos = Planogram.calculateCellPosition(layout, index)
			if (pos && pos.row === row && pos.column === column) return index
		}
	} catch {
		return null
	}
	return null
}

function endpointFromSlots(
	productId: number,
	productName: string,
	slots: AffinitySlotJson[],
	layoutCells: LayoutCellJson[],
	layoutString: string | null,
): CabinetAffinityEndpoint {
	const slot = slots[0]
	if (slot?.row != null && slot.column != null) {
		const layoutMatch = layoutCells.find(
			(c) => c.row === slot.row && c.column === slot.column,
		)
		const cellIndex = layoutMatch?.cell_index != null
			? Number(layoutMatch.cell_index)
			: cellIndexFromRowColumn(layoutString, slot.row, slot.column)
		return {
			productId,
			productName,
			row: slot.row,
			column: slot.column,
			slotLabel: slot.slot_label ?? layoutMatch?.slot_label ?? indexToSlotLabel(slot.row, slot.column),
			cellIndex,
		}
	}

	const byProduct = layoutCells.find((c) => productIdFromCell(c) === productId)
	if (byProduct && byProduct.row != null && byProduct.column != null) {
		return {
			productId,
			productName,
			row: Number(byProduct.row),
			column: Number(byProduct.column),
			slotLabel: slotLabelForCell(byProduct.row, byProduct.column, byProduct.slot_label),
			cellIndex: byProduct.cell_index != null ? Number(byProduct.cell_index) : null,
		}
	}

	return {
		productId,
		productName,
		row: null,
		column: null,
		slotLabel: null,
		cellIndex: null,
	}
}

function flattenAffinityResults(
	parsed: Record<string, unknown>,
	siteId: string | undefined,
): AffinityResultJson[] {
	const results = (parsed.results ?? []) as AffinityResultJson[]
	if (!siteId) return results

	return results
		.map((combo) => {
			if (!Array.isArray(combo.placements_by_cabinet)) return combo
			const cabinet = combo.placements_by_cabinet.find((entry) => entry.site_id === siteId)
			if (!cabinet) return null
			return {
				...combo,
				placements: cabinet.placements,
				placement_summary: cabinet.placement_summary ?? combo.placement_summary,
				suggest_move: cabinet.suggest_move ?? combo.suggest_move,
			}
		})
		.filter((combo): combo is AffinityResultJson => combo != null)
}

function extractAffinityPairs(
	affinityParsed: Record<string, unknown>,
	layoutCells: LayoutCellJson[],
	siteId: string | undefined,
	layoutString: string | null,
	maxPairs = 6,
): CabinetAffinityPair[] {
	const combos = flattenAffinityResults(affinityParsed, siteId)
	const pairs: CabinetAffinityPair[] = []

	for (const combo of combos) {
		const ids = combo.product_ids ?? []
		const names = combo.product_names ?? []
		if (ids.length < 2 || names.length < 2) continue

		const placements = combo.placements ?? []
		const endpointA = endpointFromSlots(
			Number(ids[0]),
			String(names[0]),
			placements[0]?.slots ?? [],
			layoutCells,
			layoutString,
		)
		const endpointB = endpointFromSlots(
			Number(ids[1]),
			String(names[1]),
			placements[1]?.slots ?? [],
			layoutCells,
			layoutString,
		)

		const hasPlacement = endpointA.cellIndex != null
			|| endpointB.cellIndex != null
			|| (endpointA.row != null && endpointB.row != null)
		if (!hasPlacement) continue

		pairs.push({
			index: pairs.length,
			color: CABINET_PAIR_COLORS[pairs.length % CABINET_PAIR_COLORS.length],
			productA: endpointA,
			productB: endpointB,
			lift: Number(combo.lift ?? 0),
			cooccurOrders: Number(combo.cooccur_orders ?? 0),
			placement: resolvePlacement(combo.placement_summary),
			suggestMove: combo.suggest_move ?? null,
		})

		if (pairs.length >= maxPairs) break
	}

	return pairs
}

function annotateCellsWithPairs(rows: CabinetHeatmapCell[][], pairs: CabinetAffinityPair[]): void {
	const cellByIndex = new Map<number, CabinetHeatmapCell>()
	for (const row of rows) {
		for (const cell of row) cellByIndex.set(cell.cellIndex, cell)
	}

	for (const pair of pairs) {
		for (const endpoint of [pair.productA, pair.productB]) {
			if (endpoint.cellIndex == null) continue
			const cell = cellByIndex.get(endpoint.cellIndex)
			if (!cell) continue
			if (!cell.pairIndices.includes(pair.index)) cell.pairIndices.push(pair.index)
		}
	}
}

function extractVisionWarnings(...sources: Array<Record<string, unknown> | null>): CabinetVisionWarning[] {
	const warnings: CabinetVisionWarning[] = []
	const seen = new Set<string>()
	for (const source of sources) {
		const rows = source?.adjacency_warnings
		if (!Array.isArray(rows)) continue
		for (const row of rows) {
			if (row.severity === 'low') continue
			const slotA = String(row.slot_a ?? '')
			const slotB = String(row.slot_b ?? '')
			if (!slotA || !slotB) continue
			const key = [slotA, slotB].sort().join('|')
			if (seen.has(key)) continue
			seen.add(key)
			warnings.push({
				slotA,
				slotB,
				productAName: String(row.product_a_name ?? ''),
				productBName: String(row.product_b_name ?? ''),
				reason: String(row.reason ?? 'similar_products'),
				severity: row.severity === 'high' ? 'high' : 'medium',
			})
		}
	}
	return warnings.slice(0, 8)
}

function annotateVisionRisks(rows: CabinetHeatmapCell[][], warnings: CabinetVisionWarning[]): void {
	const slotToReason = new Map<string, string>()
	for (const warning of warnings) {
		const label = `${warning.productAName} ↔ ${warning.productBName} (${warning.reason.replace(/_/g, ' ')})`
		slotToReason.set(warning.slotA, label)
		slotToReason.set(warning.slotB, label)
	}
	for (const row of rows) {
		for (const cell of row) {
			if (!cell.slotLabel) continue
			const reason = slotToReason.get(cell.slotLabel)
			if (!reason) continue
			cell.visionRisk = true
			cell.visionRiskReason = reason
		}
	}
}

interface SlotInventoryJson {
	cell_index?: number
	stock_status?: 'out' | 'low' | 'ok' | 'empty_slot'
	quantity?: number
}

function annotateStockLevels(rows: CabinetHeatmapCell[][], inventoryCells: SlotInventoryJson[]): void {
	const byIndex = new Map<number, SlotInventoryJson>()
	for (const cell of inventoryCells) {
		if (cell.cell_index == null) continue
		byIndex.set(Number(cell.cell_index), cell)
	}
	for (const row of rows) {
		for (const cell of row) {
			const inv = byIndex.get(cell.cellIndex)
			if (!inv) continue
			cell.stockStatus = inv.stock_status ?? null
			cell.quantity = inv.quantity ?? null
		}
	}
}

interface SwapJson {
	cell_index?: number
	slot_label?: string | null
	current_product_name?: string | null
	current_recipe_name?: string | null
	suggested_product_name?: string | null
	suggested_recipe_name?: string | null
	from_product_name?: string | null
	to_product_name?: string | null
	rationale?: string | null
	current_revenue_cents?: number | null
	suggested_revenue_cents?: number | null
}

function extractSwapPreviews(
	toolCalls: ToolCallLike[],
	layoutCells: LayoutCellJson[],
): CabinetSwapPreview[] {
	const swaps: CabinetSwapPreview[] = []
	const slotByIndex = new Map<number, string | null>()
	for (const cell of layoutCells) {
		if (cell.cell_index == null) continue
		slotByIndex.set(Number(cell.cell_index), cell.slot_label ?? null)
	}

	for (const tc of toolCalls) {
		if (tc.status !== 'done' || !tc.result) continue
		const parsed = parseToolJson(tc.result)
		if (!parsed) continue

		if (tc.name === 'suggest_planogram_swaps' && Array.isArray(parsed.swaps)) {
			for (const swap of parsed.swaps as SwapJson[]) {
				if (swap.cell_index == null) continue
				swaps.push({
					cellIndex: Number(swap.cell_index),
					slotLabel: swap.slot_label ?? slotByIndex.get(Number(swap.cell_index)) ?? null,
					fromProductName: swap.current_product_name ?? swap.current_recipe_name ?? null,
					toProductName: swap.suggested_product_name ?? swap.suggested_recipe_name ?? null,
					rationale: swap.rationale ?? null,
					currentRevenueCents: swap.current_revenue_cents ?? null,
					suggestedRevenueCents: swap.suggested_revenue_cents ?? null,
				})
			}
		}

		if (tc.name === 'apply_planogram_draft' && parsed.__apply_planogram_draft && Array.isArray(parsed.applied_changes)) {
			for (const change of parsed.applied_changes as SwapJson[]) {
				if (change.cell_index == null) continue
				swaps.push({
					cellIndex: Number(change.cell_index),
					slotLabel: change.slot_label ?? slotByIndex.get(Number(change.cell_index)) ?? null,
					fromProductName: change.from_product_name ?? change.from_recipe_name ?? null,
					toProductName: change.to_product_name ?? change.to_recipe_name ?? null,
				})
			}
		}
	}

	return swaps.slice(0, 12)
}

function annotateSwapPreviews(rows: CabinetHeatmapCell[][], swaps: CabinetSwapPreview[]): void {
	const byIndex = new Map(swaps.map((swap) => [swap.cellIndex, swap]))
	for (const row of rows) {
		for (const cell of row) {
			const swap = byIndex.get(cell.cellIndex)
			if (!swap) continue
			cell.swapPreview = {
				fromProductName: swap.fromProductName,
				toProductName: swap.toProductName,
			}
			if (swap.toProductName) cell.productName = swap.toProductName
		}
	}
}

function resolveSiteId(
	layoutResult: Record<string, unknown> | null,
	perfResult: Record<string, unknown> | null,
	affinityResult: Record<string, unknown> | null,
): string | undefined {
	return (
		(typeof layoutResult?.site_id === 'string' ? layoutResult.site_id : undefined)
		?? (typeof perfResult?.site_id === 'string' ? perfResult.site_id : undefined)
		?? (typeof affinityResult?.site_id === 'string' ? affinityResult.site_id : undefined)
	)
}

function resolveDateRange(
	perfResult: Record<string, unknown> | null,
	affinityResult: Record<string, unknown> | null,
): { dateFrom?: string; dateTo?: string } {
	const from = perfResult?.from ?? affinityResult?.from
	const to = perfResult?.to ?? affinityResult?.to
	return {
		dateFrom: typeof from === 'string' ? from : undefined,
		dateTo: typeof to === 'string' ? to : undefined,
	}
}

/** Build cabinet map data from assistant tool calls (layout, sales, and/or affinity pairs). */
export function extractCabinetMapFromToolCalls(
	toolCalls: ToolCallLike[] | undefined,
): CabinetMapData | null {
	if (!toolCalls?.length) return null

	let perfResult: Record<string, unknown> | null = null
	let layoutResult: Record<string, unknown> | null = null
	let affinityResult: Record<string, unknown> | null = null
	let slotInventoryResult: Record<string, unknown> | null = null

	for (const tc of toolCalls) {
		if (tc.status !== 'done' || !tc.result) continue
		const parsed = parseToolJson(tc.result)
		if (!parsed) continue
		if (tc.name === 'get_cell_performance') perfResult = parsed
		if (tc.name === 'get_planogram_layout') layoutResult = parsed
		if (tc.name === 'get_cabinet_slot_inventory') slotInventoryResult = parsed
		if (tc.name === 'get_affinity_placement' || tc.name === 'get_product_affinity') {
			affinityResult = parsed
		}
	}

	const perfCells = perfResult ? performanceCellsFromResult(perfResult) : null
	const hasPerf = Boolean(perfCells?.length)
	const perfMap = perfByCellIndex(perfCells ?? [])

	let layout = layoutResult ? layoutCellsFromResult(layoutResult) : null
	if (!layout && affinityResult) {
		layout = layoutCellsFromResult(affinityResult)
	}
	const siteId = resolveSiteId(layoutResult, perfResult, affinityResult)
		?? layout?.siteId

	let rows: CabinetHeatmapCell[][] = []
	if (layout?.cells.length) {
		rows = buildRowsFromLayoutCells(layout.cells, layout.layout, perfMap)
	} else if (hasPerf) {
		rows = buildRowsFromPerformanceOnly(perfCells!)
	}

	const pairs = affinityResult
		? extractAffinityPairs(affinityResult, layout?.cells ?? [], siteId, layout?.layout ?? null)
		: []
	const visionWarnings = extractVisionWarnings(layoutResult, affinityResult)
	const swaps = extractSwapPreviews(toolCalls, layout?.cells ?? [])
	const inventoryCells = Array.isArray(slotInventoryResult?.cells)
		? slotInventoryResult.cells as SlotInventoryJson[]
		: []

	if (!rows.length) return null
	const hasMapContent = hasPerf
		|| pairs.length > 0
		|| visionWarnings.length > 0
		|| swaps.length > 0
		|| inventoryCells.some((c) => c.stock_status === 'out' || c.stock_status === 'low')
	if (!hasMapContent) return null

	annotateCellsWithPairs(rows, pairs)
	annotateVisionRisks(rows, visionWarnings)
	annotateStockLevels(rows, inventoryCells)
	annotateSwapPreviews(rows, swaps)

	const dates = resolveDateRange(perfResult, affinityResult)
	return finalizeMap(rows, {
		siteName:
			(typeof perfResult?.site_name === 'string' ? perfResult.site_name : undefined)
			?? layout?.siteName
			?? (typeof slotInventoryResult?.site_name === 'string' ? slotInventoryResult.site_name : undefined)
			?? (typeof affinityResult?.site_name === 'string' ? affinityResult.site_name : undefined),
		...dates,
		currency: typeof perfResult?.currency === 'string' ? perfResult.currency : undefined,
	}, pairs, visionWarnings, swaps)
}

/** @deprecated Use extractCabinetMapFromToolCalls */
export function extractCabinetHeatmapFromToolCalls(
	toolCalls: ToolCallLike[] | undefined,
): CabinetMapData | null {
	return extractCabinetMapFromToolCalls(toolCalls)
}

export function heatmapCellBackground(intensity: number): string {
	const ratio = Math.max(0, Math.min(1, intensity))
	const hue = 210
	const saturation = 55 + ratio * 35
	const lightness = 94 - ratio * 48
	return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

export function heatmapCellTextColor(intensity: number): string {
	return intensity >= 0.55 ? '#fff' : 'var(--mui-palette-text-primary)'
}

export function pairCellBackground(color: string, alpha = 0.22): string {
	return `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`
}

export function formatHeatmapRevenue(cents: number, currency?: string): string {
	const amount = cents / 100
	const code = currency?.toUpperCase()
	if (code === 'CAD') return `$${amount.toFixed(0)} CAD`
	if (code === 'USD') return `$${amount.toFixed(0)}`
	return `$${amount.toFixed(0)}${code ? ` ${code}` : ''}`
}

export function pairPlacementLabel(placement: CabinetPairPlacement): string {
	if (placement === 'adjacent') return 'Adjacent slots'
	if (placement === 'same_row') return 'Same shelf'
	if (placement === 'far') return 'Different shelves'
	return 'Not in layout'
}

export function suggestMoveLabel(suggestMove: string | null): string | null {
	if (!suggestMove) return null
	if (suggestMove === 'already_adjacent') return 'Already next to each other'
	if (suggestMove === 'move_closer_same_row') return 'Could move closer on same shelf'
	if (suggestMove === 'consider_same_shelf_if_visually_distinct') return 'Consider same shelf if visually distinct'
	if (suggestMove === 'one_or_both_not_in_layout') return 'One or both not in layout'
	return suggestMove.replace(/_/g, ' ')
}
